<template>
<div>
    <div v-if="loading" class="text-center">
        <i class="fa fa-circle-o-notch fa-spin"></i> Loading
    </div>
    <div v-else>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item font-weight-light">
                    <router-link :to="`/inspections`">
                        <i class="fa fa-list"></i> Inspections
                    </router-link>
                </li>
                <li class="breadcrumb-item font-weight-light">
                    <router-link :to="`/inspections/view/${result.InspectionId}`">
                        <inspection-name :inspection="{InspectionDate: result.InspectionDate, JobsiteNumber: result.JobsiteNumber, JobsiteName: result.JobsiteName}" />
                    </router-link>
                </li>
                <li class="breadcrumb-item active">
                    Result: {{ result.sectionDisplayOrder }}.{{ result.DisplayOrder }} <span class="d-xl-inline d-none">{{ result.ElementText }}</span>
                </li>
            </ol>
        </h4>

        <div class="row">
            <div class="col-xl-9 col-lg-10">
                <b-card class="mb-4">
                    <template slot="header">
                        Result: <strong>{{ result.sectionDisplayOrder }}.{{ result.DisplayOrder }}</strong> {{ result.ElementText }}
                        <div class="float-md-right float-none mt-2 mt-md-0" v-if="this.$root.hasPermission(['inspections.view.browse', 'inspections.view.link'])">
                            <b-button :to="'/inspections/view/'+ result.InspectionId" variant="light" size="sm">
                                View Inspection
                            </b-button>
                            <b-button :to="'/inspections/edit/'+ result.InspectionId" variant="light" size="sm" v-if="this.$root.hasPermission('inspections.manage') || result.CreatedByUserId == this.$store.state.user.user_id">
                                <i class="fa fa-edit"></i> Edit Inspection
                            </b-button>
                        </div>
                    </template>

                    <div class="row">
                        <div class="col-lg-6">
                            <div class="row mb-2">
                                <div class="col-lg-4 text-muted">Inspection Date:</div>
                                <div class="col-lg-8">
                                    {{ result.InspectionDate | moment("MM/DD/YYYY") }}
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-lg-4 text-muted">Jobsite:</div>
                                <div class="col-lg-8">
                                    {{ result.JobsiteNumber }} - {{ result.JobsiteName }}
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-lg-4 text-muted">Superintendent:</div>
                                <div class="col-lg-8">{{ result.SuperFirstName }} {{ result.SuperLastName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-lg-4 text-muted">Created By:</div>
                                <div class="col-lg-8">{{ result.InspFirstName }} {{ result.InspLastName }}</div>
                            </div>
                        </div>
                        <div class="col-lg-6">
                            <div class="row mb-2">
                                <div class="col-lg-4 text-muted">Status:</div>
                                <div class="col-lg-8">
                                    <b-badge variant="success" v-if="result.ComplianceStatusId == 1">{{ labelCompliant }}</b-badge>
                                    <b-badge variant="danger" v-else-if="result.ComplianceStatusId == 2">{{ labelNonCompliant }}</b-badge>
                                    <b-badge variant="secondary" v-else>{{ labelNA }}</b-badge>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-lg-4 text-muted">Comment:</div>
                                <div class="col-lg-8">{{ result.Comment }}</div>
                            </div>
                        </div>
                    </div>
                </b-card>

                <b-card class="mb-4">
                    <strong slot="header">Result Files &amp; Photos</strong>
                    <template v-if="result.files && result.files.length > 0">
                        <file-list :files="result.files" />
                    </template>
                    <template v-else>
                        -- No Result Files/Photos --
                    </template>
                </b-card>

                <template v-if="actions.length > 0">
                    <b-card v-for="action in actions" class="mb-3" :key="action.corrective_action_id">
                        <template slot="header">
                            <div class="row">
                                <div class="col-sm-5 col-xs-12 mb-sm-0 mb-2">
                                    <strong>Corrective Action</strong>
                                </div>
                                <div class="col-sm-7 col-xs-12 text-sm-right">
                                    <router-link :to="`/corrective-actions/view/${action.corrective_action_id}`" class="btn btn-sm btn-light">
                                        View
                                    </router-link>
                                    <router-link :to="`/corrective-actions/edit/${action.corrective_action_id}`" class="btn btn-sm btn-light">
                                        <i class="fa fa-edit"></i> Edit
                                    </router-link>
                                    <router-link :to="`/corrective-actions/delete/${action.corrective_action_id}`" class="btn btn-sm btn-light">
                                        <i class="fa fa-trash"></i> Delete
                                    </router-link>
                                </div>
                            </div>
                        </template>

                        <div class="row mb-3">
                            <div class="col-lg-6 mb-lg-0 mb-2">
                                <div class="row mb-2">
                                    <div class="col text-muted">Status</div>
                                    <div class="col-md-8">
                                        <label :class="`badge badge-${action.status_id ? action.status_color : ''}`">
                                            {{ action.status_id ? action.status_name : '--' }}
                                        </label>
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col text-muted">Created By</div>
                                    <div class="col-md-8">{{ action.creator_name }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col text-muted">Assigned To</div>
                                    <div class="col-md-8">{{ action.assignee_name }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col text-muted">Completed By</div>
                                    <div class="col-md-8">{{ action.completer_name }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col text-muted">Subcontractor</div>
                                    <div class="col-md-8">{{ action.subcontractor }}</div>
                                </div>
                                <div class="row mb-2" v-if="action.source_name">
                                    <div class="col text-muted">Source</div>
                                    <div class="col-md-8">{{ action.source_name }}</div>
                                </div>
                            </div>
                            <div class="col-lg-6">
                                <div class="row mb-2">
                                    <div class="col text-muted">Created Date</div>
                                    <div class="col-md-8">{{ action.created_at | moment("timezone", userTimezone, "MM/DD/YYYY hh:mm a")  }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col text-muted">Due Due</div>
                                    <div class="col-md-8">{{ action.due_date | moment("MM/DD/YYYY")  }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col text-muted">Completed Date</div>
                                    <div class="col-md-8">{{ action.completed_at | moment("timezone", userTimezone, "MM/DD/YYYY hh:mm a")  }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col text-muted">Time to Completion</div>
                                    <div class="col-md-8">{{ action.time_to_completion  }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col text-muted">Location</div>
                                    <div class="col-md-8">{{ action.location }}</div>
                                </div>
                            </div>
                        </div>

                        <label class="text-muted">Detail:</label>
                        <div v-html="action.detail" class="mb-4"></div>

                        <label class="text-muted">Completed Detail:</label>
                        <div v-html="action.completed_detail"></div>
                        <hr/>

                        <template v-if="action.files && action.files.length > 0">
                            <file-list :files="action.files"></file-list>
                        </template>
                        <template v-else>
                            -- No Corrective Action Files --
                        </template>
                    </b-card>
                </template>
            </div>
            <div class="col-xl-2 col-lg-12">

            </div>
        </div>
    </div>
</div>
</template>

<script>
export default {
    //props: ['inspection', 'sections', 'files'],

    data() {
        return {
            loading: true,
            ResultId: this.$route.params.id,
            result: null,
            actions: [],
            userTimezone: this.$store.state.user.timezone,
            result_labels: null,
        }
    },

    computed: {
        labelCompliant(){
            //Check if set
            if(this.result_labels && this.result_labels.compliant)
            {
                return this.result_labels.compliant;
            }

            //Default
            return 'Compliant';
        },

        labelNonCompliant(){
            //Check if set
            if(this.result_labels && this.result_labels.noncompliant)
            {
                return this.result_labels.noncompliant;
            }

            //Default
            return 'Non-Compliant';
        },

        labelNA(){
            //Check if set
            if(this.result_labels && this.result_labels.na)
            {
                return this.result_labels.na;
            }

            //Default
            return 'N/A';
        },
    },

    methods: {
        getInspectionResult() {
            //Get the data
            this.$http
                .get('inspection-results/view/' + this.ResultId)
                .then(response => {
                    //Set all the data
                    this.result = response.data.result;
                    this.actions = response.data.actions;

                    //Check labels
                    if(this.result.result_labels)
                    {
                        //Set the value
                        this.result_labels = JSON.parse(this.result.result_labels);
                    }

                    //Set loading
                    this.loading = false;
                })
                .catch(response => {

                });
        },
    },
    mounted() {
        //Get the inspections
        this.getInspectionResult();
    }
}
</script>
